"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Bell,
  Bot,
  Building2,
  Check,
  Copy,
  Database,
  FileText,
  FlaskConical,
  Globe2,
  KeyRound,
  Loader2,
  Mic2,
  Pencil,
  Plus,
  Save,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  Video,
  Volume2,
  Webhook,
  X,
} from "lucide-react";
import { hasProviderMark, ProviderMark } from "@/components/provider-mark";
import { Switch } from "@/components/ui/switch";
import { Select } from "@/components/ui/select";
import { Card, PageHeader, Pill } from "@/components/pulse-shell";
import { VoicePicker } from "@/components/voice-picker";
import {
  api,
  type CredentialSlot,
  type LlmRouting,
  type OfflineAnalysis,
  type ProbeResult,
  type Settings,
} from "@/lib/api";
import { clampTtsSpeed, clampTtsVolume, type VoiceDeliveryValue } from "@/lib/voice-catalog";

const SECTION_DEFS = [
  { slug: "general", label: "General", icon: Settings2 },
  { slug: "keys", label: "API keys & secrets", icon: KeyRound },
  { slug: "ai-providers", label: "AI providers", icon: Database },
  { slug: "voice", label: "Voice & avatar", icon: Mic2 },
  { slug: "session-defaults", label: "Session defaults", icon: SlidersHorizontal },
  { slug: "security", label: "Security & privacy", icon: ShieldCheck },
  { slug: "notifications", label: "Notifications", icon: Bell },
  { slug: "webhooks", label: "API & webhooks", icon: Webhook },
] as const;

type SectionSlug = (typeof SECTION_DEFS)[number]["slug"];

const SLUG_SET = new Set<string>(SECTION_DEFS.map((s) => s.slug));

const PROVIDERS = [
  "groq",
  "cerebras",
  "fireworks",
  "gemini",
  "openai",
  "oruk",
  "mistral",
  "bedrock",
  "deepgram",
  "cartesia",
  "elevenlabs",
  "heygen",
  "liveavatar",
] as const;

const ROLE_OPTS = ["stt", "llm", "tts", "avatar", "offline", "vision"] as const;

function voiceFromSettings(s: Settings): VoiceDeliveryValue {
  return {
    voiceId: s.voiceId || null,
    voiceName: s.voiceName || null,
    ttsSpeed: clampTtsSpeed(s.ttsSpeed, 1),
    ttsVolume: clampTtsVolume(s.ttsVolume, 1),
    ttsEmotion: s.ttsEmotion || null,
  };
}

/** Video perception provider caveats (Bedrock Nova reads the full video from S3). */
const VISION_PROVIDER_LIMITS: Record<string, { short: string; detail: string }> = {
  gemini: {
    short: "Gemini gets the whole video inline: recordings over 18 MB fail.",
    detail:
      "The MP4 is loaded into Lambda memory and sent inline. Long calls produce larger files and can hit request limits; those sessions end as Video failed.",
  },
  openai: {
    short: "OpenAI sees about 10 still frames, not continuous video.",
    detail:
      "Frames are sampled evenly across the call (4 per journey stage clip). Motion, timing, and brief events between frames are not visible to the model.",
  },
};

export function SettingsPage() {
  const { tab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const sectionSlug: SectionSlug = SLUG_SET.has(tab || "")
    ? (tab as SectionSlug)
    : "general";
  const section =
    SECTION_DEFS.find((s) => s.slug === sectionSlug)?.label || "General";

  const [name, setName] = useState("Igentify Agent Operations");
  const [savedName, setSavedName] = useState(name);
  const [environment, setEnvironment] = useState("Production");
  const [toast, setToast] = useState<string | null>(null);
  const [toastTone, setToastTone] = useState<"ok" | "error">("ok");
  const [settings, setSettings] = useState<Settings>({});
  const [slots, setSlots] = useState<CredentialSlot[]>([]);
  const [slotsDraft, setSlotsDraft] = useState<CredentialSlot[]>([]);
  const [routing, setRouting] = useState<LlmRouting | null>(null);
  const [routingDraft, setRoutingDraft] = useState<LlmRouting | null>(null);
  const [offline, setOffline] = useState<OfflineAnalysis | null>(null);
  const [offlineDraft, setOfflineDraft] = useState<OfflineAnalysis | null>(null);
  const [catalog, setCatalog] = useState<Settings["modelCatalog"]>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [probing, setProbing] = useState(false);
  const [probingRow, setProbingRow] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [secretEdits, setSecretEdits] = useState<Record<string, string>>({});
  const [testingSlotId, setTestingSlotId] = useState<string | null>(null);
  const [voiceDraft, setVoiceDraft] = useState<VoiceDeliveryValue>({
    voiceId: null,
    voiceName: null,
    ttsSpeed: 1,
    ttsVolume: 1,
    ttsEmotion: null,
  });
  const [voiceSaved, setVoiceSaved] = useState<VoiceDeliveryValue>(voiceDraft);

  const dirtyGeneral = name !== savedName;
  const dirtyKeys = useMemo(
    () => JSON.stringify(slots) !== JSON.stringify(slotsDraft) || Object.keys(secretEdits).length > 0,
    [slots, slotsDraft, secretEdits],
  );
  const dirtyRouting = useMemo(
    () =>
      JSON.stringify(routing) !== JSON.stringify(routingDraft) ||
      JSON.stringify(offline) !== JSON.stringify(offlineDraft),
    [routing, routingDraft, offline, offlineDraft],
  );
  const dirtyVoice = useMemo(
    () => JSON.stringify(voiceDraft) !== JSON.stringify(voiceSaved),
    [voiceDraft, voiceSaved],
  );

  /** Live pipeline Avatar track — LiveAvatar LITE when a credential exists; else audio-only. */
  const avatarPipelineNodes = useMemo(() => {
    const avatarSlots = slots.filter((s) => {
      const p = String(s.provider || "").toLowerCase();
      const roles = s.roles || [];
      if (p === "liveavatar") return true;
      if (p === "heygen" && (roles.includes("avatar") || !roles.length)) return true;
      return roles.includes("avatar");
    });
    const preferred =
      avatarSlots.find((s) => String(s.provider || "").toLowerCase() === "liveavatar")
      || avatarSlots[0];
    if (preferred?.preview) {
      const provider = String(preferred.provider || "liveavatar").toLowerCase();
      const title = provider === "heygen" ? "HeyGen / LiveAvatar" : "LiveAvatar";
      return [{
        title,
        meta: "LITE · per-agent enable",
        tone: "fixed" as const,
      }];
    }
    return [{
      title: "Audio only",
      meta: "Add liveavatar key → API keys",
      tone: "fixed" as const,
    }];
  }, [slots]);

  const showToast = (msg: string, tone: "ok" | "error" = "ok") => {
    setToastTone(tone);
    setToast(msg);
    window.setTimeout(() => setToast(null), tone === "error" ? 4500 : 2500);
  };

  function routingDuplicateError(chain: LlmRouting["chain"] | undefined): string | null {
    const seen = new Set<string>();
    for (const c of chain || []) {
      const key = `${c.slotId}::${c.modelId}`;
      if (seen.has(key)) {
        return "Each failover entry must use a different credential and model. Change or remove the duplicate, then save.";
      }
      seen.add(key);
    }
    return null;
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await api.getSettings();
      setSettings(s);
      setSlots(s.credentialSlots || []);
      setSlotsDraft(structuredClone(s.credentialSlots || []));
      setRouting(s.llmRouting || null);
      setRoutingDraft(structuredClone(s.llmRouting || null));
      setOffline(s.offlineAnalysis || null);
      setOfflineDraft(structuredClone(s.offlineAnalysis || null));
      setCatalog(s.modelCatalog || {});
      const v = voiceFromSettings(s);
      setVoiceDraft(v);
      setVoiceSaved(v);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveKeys() {
    setSaving(true);
    try {
      const payload = slotsDraft.map((s) => ({
        ...s,
        secret: secretEdits[s.id],
      }));
      const deleted = slots.filter((s) => !slotsDraft.some((d) => d.id === s.id)).map((s) => s.id);
      const { slots: next } = await api.putCredentials({
        slots: payload,
        deleteIds: deleted,
      });
      setSlots(next);
      setSlotsDraft(structuredClone(next));
      setSecretEdits({});
      showToast("API keys saved");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function saveVoice() {
    setSaving(true);
    try {
      const next = await api.putSettings({
        ...settings,
        voiceId: voiceDraft.voiceId,
        voiceName: voiceDraft.voiceName,
        ttsSpeed: clampTtsSpeed(voiceDraft.ttsSpeed, 1),
        ttsVolume: clampTtsVolume(voiceDraft.ttsVolume, 1),
        ttsEmotion: voiceDraft.ttsEmotion || null,
      });
      setSettings(next);
      const v = voiceFromSettings(next);
      setVoiceDraft(v);
      setVoiceSaved(v);
      showToast("Workspace voice saved");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Save failed", "error");
    } finally {
      setSaving(false);
    }
  }

  function onVoiceDraftChange(next: VoiceDeliveryValue) {
    setVoiceDraft(next);
  }

  async function saveRouting() {
    if (!routingDraft) {
      showToast("Routing not loaded yet", "error");
      return;
    }
    const offlinePayload = offlineDraft || offline;
    if (!offlinePayload) {
      showToast("Offline analysis not loaded yet", "error");
      return;
    }
    const dup = routingDuplicateError(routingDraft.chain);
    if (dup) {
      showToast(dup, "error");
      return;
    }
    setSaving(true);
    try {
      const next = await api.putSettings({
        ...settings,
        llmRouting: { ...routingDraft, autoPrioritize: false },
        offlineAnalysis: offlinePayload,
      });
      setSettings(next);
      setRouting(next.llmRouting || null);
      setRoutingDraft(structuredClone(next.llmRouting || null));
      setOffline(next.offlineAnalysis || null);
      setOfflineDraft(structuredClone(next.offlineAnalysis || null));
      setCatalog(next.modelCatalog || catalog);
      setEditOpen(false);
      showToast("Provider routing saved");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Save failed", "error");
    } finally {
      setSaving(false);
    }
  }

  async function applyProbeResults(
    results: ProbeResult[],
    measuredAt: string,
    base: LlmRouting,
  ) {
    const byKey: LlmRouting["lastProbeByEntry"] = { ...(base.lastProbeByEntry || {}) };
    for (const r of results) {
      if (!r.ok || r.p50Ms == null) continue;
      byKey[`${r.slotId}:${r.modelId}`] = {
        p50Ms: r.p50Ms,
        p90Ms: r.p90Ms || r.p50Ms,
        verdict: r.verdict || "unknown",
        measuredAt: r.measuredAt || measuredAt,
      };
    }
    setRoutingDraft({
      ...base,
      autoPrioritize: false,
      lastProbeAt: measuredAt,
      lastProbeByEntry: byKey,
    });
  }

  async function runBulkProbe() {
    if (!routingDraft?.chain?.length) return;
    setProbing(true);
    setProbingRow(null);
    try {
      const probed = await api.probeProviders({
        modality: "llm",
        entries: routingDraft.chain.map((c) => ({ slotId: c.slotId, modelId: c.modelId })),
        runs: 4,
      });
      await applyProbeResults(probed.results, probed.measuredAt, routingDraft);
      const failed = probed.results.filter((r) => !r.ok).length;
      showToast(failed ? `Probe done · ${failed} failed` : "Probe complete");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Probe failed");
    } finally {
      setProbing(false);
    }
  }

  async function runRowProbe(slotId: string, modelId: string) {
    const key = `${slotId}:${modelId}`;
    if (!routingDraft) return;
    setProbingRow(key);
    try {
      const probed = await api.probeProviders({
        modality: "llm",
        entries: [{ slotId, modelId }],
        runs: 3,
      });
      await applyProbeResults(probed.results, probed.measuredAt, routingDraft);
      const r = probed.results[0];
      if (r && !r.ok) showToast(r.error || "Probe failed");
      else showToast("Probe complete");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Probe failed");
    } finally {
      setProbingRow(null);
    }
  }

  async function testSlot(slotId: string) {
    setTestingSlotId(slotId);
    try {
      const result = await api.testCredential(slotId);
      if (result.ok) {
        showToast(
          `OK · ${result.provider}${result.latencyMs != null ? ` · ${Math.round(result.latencyMs)}ms` : ""}`,
        );
      } else {
        showToast(result.error || "Credential test failed");
      }
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Credential test failed");
    } finally {
      setTestingSlotId(null);
    }
  }

  if (!tab || !SLUG_SET.has(tab)) {
    return <Navigate to="/settings/general" replace />;
  }

  return (
    <>
      <PageHeader title="Settings" subtitle="Configure workspace-wide behavior and integrations." />
      <div className="settings-layout">
        <aside className="card settings-nav">
          {SECTION_DEFS.map(({ slug, label, icon: Icon }) => (
            <button
              key={slug}
              type="button"
              className={sectionSlug === slug ? "active" : ""}
              onClick={() => navigate(`/settings/${slug}`)}
            >
              <Icon />
              {label}
            </button>
          ))}
        </aside>

        {section === "General" ? (
          <div className="settings-main">
            <Card className="settings-card" title="Workspace profile">
              <div className="workspace-profile">
                <div className="workspace-emblem">
                  <Building2 />
                  <button className="workspace-edit" aria-label="Edit workspace icon">
                    <Pencil size={13} />
                  </button>
                </div>
                <div>
                  <div className="settings-fields">
                    <label className="setting-field">
                      Workspace name
                      <input className="setting-input" value={name} onChange={(e) => setName(e.target.value)} />
                      <small>This name is shown across your workspace.</small>
                    </label>
                    <label className="setting-field">
                      Workspace ID
                      <div style={{ position: "relative" }}>
                        <input className="setting-input" readOnly value="ws_igentify_prod" />
                        <Copy size={13} style={{ position: "absolute", right: 10, top: 11, color: "#536c89" }} />
                      </div>
                      <small>Used for API integrations and support.</small>
                    </label>
                  </div>
                  <div className="settings-fields three">
                    <div className="setting-field">
                      <Select
                        label="Default timezone"
                        triggerClassName="setting-input"
                        searchable={false}
                        value="Europe / Warsaw (UTC+2)"
                        onChange={() => {}}
                        options={[{ value: "Europe / Warsaw (UTC+2)", label: "Europe / Warsaw (UTC+2)" }]}
                      />
                      <small>Used for logs, reports and scheduled tasks.</small>
                    </div>
                    <div className="setting-field">
                      <Select
                        label="Default language"
                        triggerClassName="setting-input"
                        searchable={false}
                        value="English (US)"
                        onChange={() => {}}
                        options={[{ value: "English (US)", label: "English (US)" }]}
                      />
                      <small>Default language for agents and UI.</small>
                    </div>
                    <label className="setting-field">
                      Environment
                      <div className="env-segment">
                        {["Production", "Staging", "Development"].map((x) => (
                          <button
                            key={x}
                            className={environment === x ? "active" : ""}
                            onClick={() => setEnvironment(x)}
                          >
                            {x}
                          </button>
                        ))}
                      </div>
                      <small>Determines which resources and data are used.</small>
                    </label>
                  </div>
                </div>
              </div>
            </Card>
            <div className="settings-two">
              <Card
                className="settings-card"
                title="Default session capabilities"
                kicker="Applies to new agents; agent-level overrides remain unchanged."
              >
                <Capability icon={<Mic2 />} name="Record sessions" note="Capture dual-channel audio by default." />
                <Capability icon={<FileText />} name="Generate transcripts" note="Real-time STT with speaker separation." />
                <Capability icon={<Bot />} name="Perception analysis" note="Combine voice, face, language and body signals." />
                <Capability icon={<KeyRound />} name="Require recording consent" note="Block recording until consent is verified." />
              </Card>
              <Card
                className="settings-card"
                title="Global provider routing"
                action={
                  <button
                    type="button"
                    className="linkish"
                    onClick={() => {
                      navigate("/settings/ai-providers");
                      setEditOpen(true);
                    }}
                  >
                    Edit LLM chain →
                  </button>
                }
              >
                <div className="prov-board compact">
                  <ModalityTrack
                    icon={<Mic2 />}
                    name="STT"
                    nodes={[{ title: "Deepgram", meta: "Nova-2", tone: "fixed" }]}
                  />
                  <ModalityTrack
                    icon={<Bot />}
                    name="LLM"
                    failover
                    nodes={
                      !(routingDraft?.chain?.length)
                        ? [{ title: "—", meta: "Not configured", tone: "fixed" }]
                        : routingDraft.chain.map((c, i) => {
                            const slot = slots.find((s) => s.id === c.slotId);
                            const probe =
                              routingDraft.lastProbeByEntry?.[`${c.slotId}:${c.modelId}`];
                            return {
                              title: slot?.provider || "llm",
                              meta: c.modelId,
                              tone: (i === 0 ? "primary" : "failover") as "primary" | "failover",
                              badge: i === 0 ? "Primary" : `F${i}`,
                              probe: probe ? `${probe.p50Ms}ms` : undefined,
                            };
                          })
                    }
                  />
                  <ModalityTrack
                    icon={<Volume2 />}
                    name="TTS"
                    nodes={[{
                      title: "Cartesia",
                      meta: voiceSaved.voiceName
                        ? `${voiceSaved.voiceName} · sonic-3.5`
                        : "sonic-3.5",
                      tone: "fixed",
                    }]}
                  />
                  <ModalityTrack
                    icon={<Video />}
                    name="Avatar"
                    nodes={avatarPipelineNodes}
                  />
                </div>
              </Card>
            </div>
            <Card className="settings-card" title="Data residency & privacy">
              <div className="residency-grid">
                <Residence icon={<Globe2 />} label="Data region" value="EU Central (Frankfurt)" note="All data is stored and processed in this region." />
                <Residence icon={<KeyRound />} label="Encryption" value="Platform-managed keys · AES-256" note="Data is encrypted in transit and at rest." />
                <Residence icon={<FileText />} label="Processing mode" value="Strict healthcare" note="Enhanced safeguards for sensitive data." />
                <div>
                  <Pill tone="green">
                    <Check size={11} />
                    Policy compliant
                  </Pill>
                </div>
              </div>
            </Card>
          </div>
        ) : null}

        {section === "API keys & secrets" ? (
          <div className="settings-main">
            <Card
              className="settings-card"
              title="API keys & secrets"
              kicker="Secrets are stored in AWS Secrets Manager. Only masked previews are shown here."
              action={
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    const id = `slot_${Math.random().toString(36).slice(2, 8)}`;
                    setSlotsDraft((d) => [
                      ...d,
                      {
                        id,
                        label: "New credential",
                        provider: "groq",
                        roles: ["llm"],
                        preview: "",
                        status: "active",
                      },
                    ]);
                  }}
                >
                  <Plus size={14} /> Add slot
                </button>
              }
            >
              {loading ? (
                <p className="muted">Loading…</p>
              ) : (
                <div className="cred-table">
                  <div className="cred-head">
                    <span>Label</span>
                    <span>Provider</span>
                    <span>Roles</span>
                    <span>Key</span>
                    <span>Actions</span>
                  </div>
                  {slotsDraft.map((s) => (
                    <div key={s.id} className="cred-row">
                      <input
                        className="setting-input"
                        value={s.label}
                        onChange={(e) =>
                          setSlotsDraft((all) =>
                            all.map((x) => (x.id === s.id ? { ...x, label: e.target.value } : x)),
                          )
                        }
                      />
                      <div className="route-provider">
                        <ProviderMark provider={s.provider} />
                        <Select
                          triggerClassName="setting-input"
                          value={s.provider}
                          onChange={(v) =>
                            setSlotsDraft((all) =>
                              all.map((x) => (x.id === s.id ? { ...x, provider: v ?? x.provider } : x)),
                            )
                          }
                          options={PROVIDERS.map((p) => ({
                            value: p,
                            label: p,
                            icon: hasProviderMark(p) ? <ProviderMark provider={p} size={18} /> : undefined,
                          }))}
                        />
                      </div>
                      <div className="role-chips">
                        {ROLE_OPTS.map((r) => {
                          const on = (s.roles || []).includes(r);
                          return (
                            <button
                              key={r}
                              type="button"
                              className={on ? "chip on" : "chip"}
                              onClick={() =>
                                setSlotsDraft((all) =>
                                  all.map((x) => {
                                    if (x.id !== s.id) return x;
                                    const roles = on
                                      ? (x.roles || []).filter((y) => y !== r)
                                      : [...(x.roles || []), r];
                                    return { ...x, roles };
                                  }),
                                )
                              }
                            >
                              {r}
                            </button>
                          );
                        })}
                      </div>
                      <input
                        className="setting-input"
                        type="password"
                        placeholder={s.preview || "Paste new secret to rotate"}
                        value={secretEdits[s.id] || ""}
                        onChange={(e) =>
                          setSecretEdits((m) => ({ ...m, [s.id]: e.target.value }))
                        }
                      />
                      <div className="cred-actions">
                        <button
                          type="button"
                          className="btn small"
                          disabled={testingSlotId === s.id || Boolean(secretEdits[s.id])}
                          title={
                            secretEdits[s.id]
                              ? "Save the new secret before testing"
                              : "Validate this credential"
                          }
                          onClick={() => void testSlot(s.id)}
                        >
                          {testingSlotId === s.id ? (
                            <Loader2 size={12} className="spin" />
                          ) : (
                            <FlaskConical size={12} />
                          )}
                          Test
                        </button>
                        <button
                          type="button"
                          className="btn icon"
                          aria-label="Delete"
                          onClick={() => setSlotsDraft((all) => all.filter((x) => x.id !== s.id))}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        ) : null}

        {section === "AI providers" ? (
          <div className="settings-main">
            <Card className="settings-card" title="Live pipeline">
              <div className="prov-board">
                <ModalityTrack
                  icon={<Mic2 />}
                  name="STT"
                  nodes={[{ title: "Deepgram", meta: "Nova-2", tone: "fixed" }]}
                />
                <ModalityTrack
                  icon={<Bot />}
                  name="LLM"
                  failover
                  action={
                    <button type="button" className="btn small" onClick={() => setEditOpen(true)}>
                      Edit chain
                    </button>
                  }
                  nodes={
                    !(routingDraft?.chain?.length)
                      ? [{ title: "—", meta: "Not configured", tone: "fixed" as const }]
                      : routingDraft.chain.map((c, i) => {
                          const slot = slots.find((s) => s.id === c.slotId);
                          const probe =
                            routingDraft.lastProbeByEntry?.[`${c.slotId}:${c.modelId}`];
                          return {
                            title: slot?.provider || "llm",
                            meta: c.modelId,
                            tone: (i === 0 ? "primary" : "failover") as "primary" | "failover",
                            badge: i === 0 ? "Primary" : `Failover ${i}`,
                            probe: probe ? `${probe.p50Ms}ms` : undefined,
                          };
                        })
                  }
                />
                <ModalityTrack
                  icon={<Volume2 />}
                  name="TTS"
                  nodes={[{ title: "Cartesia", meta: "sonic-3.5", tone: "fixed" }]}
                />
                <ModalityTrack
                  icon={<Video />}
                  name="Avatar"
                  nodes={avatarPipelineNodes}
                />
              </div>
            </Card>

            <Card className="settings-card" title="After the call">
              {offlineDraft ? (
                <div className="offline-board">
                  <OfflineFlow
                    title="Topic"
                    value={offlineDraft.topic}
                    slots={slots.filter(
                      (s) => (s.roles || []).includes("offline") || (s.roles || []).includes("llm"),
                    )}
                    models={catalog?.offline || {}}
                    onChange={(next) =>
                      setOfflineDraft((o) => (o ? { ...o, topic: next } : o))
                    }
                  />
                  <OfflineFlow
                    title="Journey"
                    value={offlineDraft.journey}
                    slots={slots.filter(
                      (s) => (s.roles || []).includes("offline") || (s.roles || []).includes("llm"),
                    )}
                    models={catalog?.offline || {}}
                    onChange={(next) =>
                      setOfflineDraft((o) => (o ? { ...o, journey: next } : o))
                    }
                  />
                  {(() => {
                    const visionSlots = slots.filter((s) => {
                      const roles = s.roles || [];
                      if (roles.includes("vision")) return true;
                      return Boolean((catalog?.vision || {})[s.provider]);
                    });
                    const videoValue = offlineDraft.video || {
                      slotId: visionSlots[0]?.id || "",
                      modelId: "",
                    };
                    if (!visionSlots.length) {
                      return (
                        <div className="offline-row">
                          <b className="prov-name">Video</b>
                          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                            No vision credentials yet. Add a key under{" "}
                            <button
                              type="button"
                              className="linkish"
                              style={{ background: "none", border: 0, padding: 0, color: "inherit", textDecoration: "underline", cursor: "pointer" }}
                              onClick={() => navigate("/settings/keys")}
                            >
                              API keys &amp; secrets
                            </button>{" "}
                            with the <b>vision</b> role (Bedrock IAM is seeded automatically on next load).
                          </p>
                        </div>
                      );
                    }
                    const value = visionSlots.some((s) => s.id === videoValue.slotId)
                      ? videoValue
                      : {
                          slotId: visionSlots[0].id,
                          modelId: (catalog?.vision?.[visionSlots[0].provider] || [])[0] || videoValue.modelId,
                        };
                    const provider = visionSlots.find((s) => s.id === value.slotId)?.provider || "";
                    const limitation = VISION_PROVIDER_LIMITS[provider];
                    return (
                      <OfflineFlow
                        title="Video"
                        value={value}
                        slots={visionSlots}
                        models={catalog?.vision || {}}
                        onChange={(next) =>
                          setOfflineDraft((o) => (o ? { ...o, video: next } : o))
                        }
                        note={
                          limitation ? (
                            <p className="offline-row-note" title={limitation.detail}>
                              <AlertTriangle size={14} aria-hidden="true" />
                              <span>{limitation.short}</span>
                            </p>
                          ) : null
                        }
                      />
                    );
                  })()}
                </div>
              ) : (
                <p className="muted">Loading…</p>
              )}
            </Card>
          </div>
        ) : null}

        {section === "Voice & avatar" ? (
          <div className="settings-main">
            <Card
              className="settings-card"
              title="Workspace voice"
              kicker="Default voice and delivery for agents that do not set their own."
            >
              {loading ? (
                <p className="muted">Loading…</p>
              ) : (
                <VoicePicker
                  layout="full"
                  clearLabel="Clear selection"
                  clearHint="Agents without their own voice will use the platform fallback."
                  unsetTitle="No workspace voice"
                  unsetHint="Pick a default voice for agents that do not override it."
                  value={voiceDraft}
                  onChange={onVoiceDraftChange}
                />
              )}
            </Card>
          </div>
        ) : null}

        {section !== "General" &&
        section !== "API keys & secrets" &&
        section !== "AI providers" &&
        section !== "Voice & avatar" ? (
          <CategorySettings title={section} />
        ) : null}
      </div>

      {/* Hide page savebar while the routing modal is open — otherwise it shows
          through the dim backdrop and clicks hit the backdrop (close, no save). */}
      {!editOpen &&
      ((section === "General" && dirtyGeneral) ||
        (section === "API keys & secrets" && dirtyKeys) ||
        (section === "AI providers" && dirtyRouting) ||
        (section === "Voice & avatar" && dirtyVoice)) ? (
        <div className="settings-savebar">
          <div className="validation">
            <i>✓</i>
            <span>
              <b>Configuration changed</b>
              <span>Review and save your workspace updates.</span>
            </span>
          </div>
          <div className="head-actions">
            <button
              type="button"
              className="btn"
              onClick={() => {
                if (section === "General") setName(savedName);
                if (section === "API keys & secrets") {
                  setSlotsDraft(structuredClone(slots));
                  setSecretEdits({});
                }
                if (section === "AI providers") {
                  setRoutingDraft(structuredClone(routing));
                  setOfflineDraft(structuredClone(offline));
                }
                if (section === "Voice & avatar") setVoiceDraft(voiceSaved);
              }}
            >
              Discard changes
            </button>
            <button
              type="button"
              className="btn primary"
              disabled={saving}
              onClick={() => {
                if (section === "General") {
                  setSavedName(name);
                  showToast("Workspace settings saved");
                } else if (section === "API keys & secrets") void saveKeys();
                else if (section === "AI providers") void saveRouting();
                else if (section === "Voice & avatar") void saveVoice();
              }}
            >
              <Save />
              Save changes
            </button>
          </div>
        </div>
      ) : null}

      {editOpen && routingDraft ? (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => {
            /* Keep draft; page savebar appears after close if dirty. */
            setEditOpen(false);
          }}
        >
          <div
            className="modal card routing-modal"
            role="dialog"
            aria-label="Edit LLM routing"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="modal-head routing-modal-head">
              <div>
                <b>LLM failover order</b>
                <span className="muted">Top = primary. Lower rows run only if higher ones fail.</span>
              </div>
              <button type="button" className="btn icon" onClick={() => setEditOpen(false)}>
                <X size={16} />
              </button>
            </header>
            <div className="route-edit-list">
              {(() => {
                const measured = (routingDraft.chain || [])
                  .map((c) => routingDraft.lastProbeByEntry?.[`${c.slotId}:${c.modelId}`]?.p50Ms)
                  .filter((v): v is number => typeof v === "number" && !Number.isNaN(v));
                const best = measured.length ? Math.min(...measured) : null;
                const worst = measured.length ? Math.max(...measured) : null;
                return routingDraft.chain.map((c, idx) => {
                  const slot = slots.find((s) => s.id === c.slotId);
                  const probeKey = `${c.slotId}:${c.modelId}`;
                  const probe = routingDraft.lastProbeByEntry?.[probeKey];
                  const models = catalog?.realtime?.[slot?.provider || ""] || [];
                  const rowBusy = probing || probingRow === probeKey;
                  let probeTone: "best" | "worst" | "mid" | "empty" = "empty";
                  if (probe?.p50Ms != null && best != null && worst != null) {
                    if (measured.length === 1 || probe.p50Ms === best) probeTone = "best";
                    else if (probe.p50Ms === worst) probeTone = "worst";
                    else probeTone = "mid";
                  }
                  return (
                    <div key={`${c.slotId}-${idx}`} className="route-edit-row">
                      <span className="prio">#{idx + 1}</span>
                      <label className="route-field">
                        <span>Provider</span>
                        <div className="route-provider">
                          <ProviderMark provider={slot?.provider || "groq"} />
                          <Select
                            triggerClassName="setting-input"
                            value={c.slotId}
                            disabled={rowBusy}
                            onChange={(slotId) => {
                              if (!slotId) return;
                              const sp = slots.find((s) => s.id === slotId);
                              const modelId =
                                (catalog?.realtime?.[sp?.provider || ""] || [])[0] || c.modelId;
                              setRoutingDraft((r) => {
                                if (!r) return r;
                                const chain = r.chain.map((x, i) =>
                                  i === idx ? { ...x, slotId, modelId, priority: i + 1 } : x,
                                );
                                return { ...r, chain };
                              });
                            }}
                            options={slots
                              .filter((s) => (s.roles || []).includes("llm"))
                              .map((s) => ({
                                value: s.id,
                                label: `${s.label} (${s.provider})`,
                                icon: hasProviderMark(s.provider) ? (
                                  <ProviderMark provider={s.provider} size={18} />
                                ) : undefined,
                              }))}
                          />
                        </div>
                      </label>
                      <label className="route-field">
                        <span>Model</span>
                        <Select
                          triggerClassName="setting-input"
                          value={c.modelId}
                          disabled={rowBusy}
                          onChange={(modelId) => {
                            if (!modelId) return;
                            setRoutingDraft((r) => {
                              if (!r) return r;
                              const chain = r.chain.map((x, i) =>
                                i === idx ? { ...x, modelId } : x,
                              );
                              return { ...r, chain };
                            });
                          }}
                          options={(models.includes(c.modelId) ? models : [c.modelId, ...models]).map(
                            (m) => ({ value: m, label: m }),
                          )}
                        />
                      </label>
                      <span className={`probe-cell ${probeTone}`}>
                        {probe?.p50Ms != null
                          ? `${Math.round(probe.p50Ms)}ms`
                          : "Not measured yet"}
                      </span>
                      <div className="row-actions">
                        <button
                          type="button"
                          className="btn icon"
                          title="Probe this entry"
                          disabled={rowBusy || probing}
                          onClick={() => void runRowProbe(c.slotId, c.modelId)}
                        >
                          {probingRow === probeKey ? (
                            <Loader2 size={14} className="spin" />
                          ) : (
                            <FlaskConical size={14} />
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn icon"
                          disabled={idx === 0 || rowBusy}
                          title="Move up"
                          onClick={() =>
                            setRoutingDraft((r) => {
                              if (!r) return r;
                              const chain = [...r.chain];
                              [chain[idx - 1], chain[idx]] = [chain[idx], chain[idx - 1]];
                              return {
                                ...r,
                                chain: chain.map((x, i) => ({ ...x, priority: i + 1 })),
                              };
                            })
                          }
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          className="btn icon"
                          disabled={idx >= routingDraft.chain.length - 1 || rowBusy}
                          title="Move down"
                          onClick={() =>
                            setRoutingDraft((r) => {
                              if (!r) return r;
                              const chain = [...r.chain];
                              [chain[idx + 1], chain[idx]] = [chain[idx], chain[idx + 1]];
                              return {
                                ...r,
                                chain: chain.map((x, i) => ({ ...x, priority: i + 1 })),
                              };
                            })
                          }
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="btn icon"
                          disabled={routingDraft.chain.length <= 1 || rowBusy}
                          title="Remove"
                          onClick={() =>
                            setRoutingDraft((r) => {
                              if (!r) return r;
                              const chain = r.chain
                                .filter((_, i) => i !== idx)
                                .map((x, i) => ({ ...x, priority: i + 1 }));
                              return { ...r, chain };
                            })
                          }
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
            <div className="modal-actions">
              <button
                type="button"
                className="btn"
                disabled={probing || Boolean(probingRow)}
                onClick={() => {
                  const llmSlots = slots.filter((s) => (s.roles || []).includes("llm"));
                  const s0 = llmSlots[0];
                  if (!s0) return;
                  const modelId =
                    (catalog?.realtime?.[s0.provider] || [])[0] || "qwen/qwen3.8-27b";
                  setRoutingDraft((r) => {
                    if (!r) return r;
                    return {
                      ...r,
                      chain: [
                        ...r.chain,
                        { priority: r.chain.length + 1, slotId: s0.id, modelId },
                      ],
                    };
                  });
                }}
              >
                <Plus size={14} /> Add failover
              </button>
              <button
                type="button"
                className="btn"
                disabled={probing || Boolean(probingRow)}
                onClick={() => void runBulkProbe()}
              >
                {probing ? (
                  <>
                    <Loader2 size={14} className="spin" /> Probing…
                  </>
                ) : (
                  <>
                    <FlaskConical size={14} /> Bulk probe
                  </>
                )}
              </button>
              <div style={{ flex: 1 }} />
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setRoutingDraft(structuredClone(routing));
                  setEditOpen(false);
                }}
              >
                Discard
              </button>
              <button
                type="button"
                className="btn primary"
                disabled={saving || probing || Boolean(probingRow)}
                onClick={() => void saveRouting()}
              >
                <Save size={14} /> Save changes
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {toast && (
        <div className={`toast${toastTone === "error" ? " toast-error" : ""}`}>
          <span className="toast-icon">{toastTone === "error" ? "!" : "✓"}</span>
          <span>
            <b>{toast}</b>
          </span>
        </div>
      )}
    </>
  );
}

function ModalityTrack({
  icon,
  name,
  nodes,
  failover,
  action,
}: {
  icon: React.ReactNode;
  name: string;
  nodes: Array<{
    title: string;
    meta: string;
    tone: "primary" | "failover" | "fixed";
    badge?: string;
    probe?: string;
  }>;
  failover?: boolean;
  action?: React.ReactNode;
}) {
  const showChain = Boolean(failover && nodes.length > 1);
  return (
    <div className={`prov-row${showChain ? " is-chain" : ""}`}>
      <span className="cap-icon">{icon}</span>
      <b className="prov-name">{name}</b>
      <div className="prov-flow" aria-label={`${name} track`}>
        {nodes.map((n, i) => (
          <div key={`${n.title}-${n.meta}-${i}`} className="prov-flow-item">
            {i > 0 ? <ArrowRight size={14} className="prov-then" aria-hidden="true" /> : null}
            <div className={`prov-chip ${n.tone}`}>
              {n.badge && showChain ? <em>{n.badge}</em> : null}
              {hasProviderMark(n.title) ? <ProviderMark provider={n.title} size={18} /> : null}
              <strong>{n.title}</strong>
              {n.meta && n.meta !== "—" ? <span>{n.meta}</span> : null}
              {n.probe ? <small>{n.probe}</small> : null}
            </div>
          </div>
        ))}
      </div>
      {action ? <div className="prov-row-action">{action}</div> : null}
    </div>
  );
}

function OfflineFlow({
  title,
  value,
  slots,
  models,
  onChange,
  note,
}: {
  title: string;
  value: { slotId: string; modelId: string };
  slots: CredentialSlot[];
  models: Record<string, string[]>;
  onChange: (v: { slotId: string; modelId: string }) => void;
  note?: React.ReactNode;
}) {
  const slot = slots.find((s) => s.id === value.slotId) || slots[0];
  const list = models[slot?.provider || ""] || [];
  const modelOpts = list.includes(value.modelId) ? list : [value.modelId, ...list];

  return (
    <div className="offline-row">
      <b className="prov-name">{title}</b>
      <label className="route-field">
        <span>Provider</span>
        <div className="route-provider">
          <ProviderMark provider={slot?.provider || "groq"} />
          <Select
            triggerClassName="setting-input"
            value={value.slotId}
            onChange={(slotId) => {
              if (!slotId) return;
              const sp = slots.find((s) => s.id === slotId);
              const modelId = (models[sp?.provider || ""] || [])[0] || value.modelId;
              onChange({ slotId, modelId });
            }}
            options={slots.map((s) => ({
              value: s.id,
              label: `${s.label} (${s.provider})`,
              icon: hasProviderMark(s.provider) ? (
                <ProviderMark provider={s.provider} size={18} />
              ) : undefined,
            }))}
          />
        </div>
      </label>
      <label className="route-field">
        <span>Model</span>
        <Select
          triggerClassName="setting-input"
          value={value.modelId}
          onChange={(modelId) => {
            if (!modelId) return;
            onChange({ ...value, modelId });
          }}
          options={modelOpts.map((m) => ({ value: m, label: m }))}
        />
      </label>
      {note}
    </div>
  );
}

function Capability({ icon, name, note }: { icon: React.ReactNode; name: string; note: string }) {
  return (
    <div className="cap-row">
      <span className="cap-icon">{icon}</span>
      <span className="cap-copy">
        <b>{name}</b>
        <span>{note}</span>
      </span>
      <Switch defaultChecked />
    </div>
  );
}

function Residence({
  icon,
  label,
  value,
  note,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  note: string;
}) {
  return (
    <div className="residency-item">
      {icon}
      <span>
        {label}
        <b>{value}</b>
        <span>{note}</span>
      </span>
    </div>
  );
}

function CategorySettings({ title }: { title: string }) {
  return (
    <div className="settings-main">
      <Card title={title} kicker={`Workspace-wide ${title.toLowerCase()} controls`}>
        <div className="switch-row">
          <span className="switch-copy">
            <b>Enforce workspace policy</b>
            <span>Apply the current policy to every production agent.</span>
          </span>
          <Switch defaultChecked />
        </div>
      </Card>
    </div>
  );
}
